import ListActions from "modules/list/actions";
import { CLUSTER_DEFINITIONS } from "utils/constants/routes";
import {
  TemplateSchema,
  ClusterProfileSchema,
  ClusterProfileTemplateSchema,
  PolicySchema,
  ClusterMetaSchema,
  VariableSchema,
} from "utils/schemas";
import { generatePath } from "react-router";
import createFormActions from "modules/form/actions";
import { parseTagsForInput } from "utils/parsers";
import historyService from "services/history";
import {
  Missing,
  DebouncedRule,
  areValidClusterProfileTags,
  isValidName,
  KUBERNETES_NAME_REGEX,
  KUBERNETES_NAME_RULES,
  isValidVariable,
} from "services/validator/rules";
import store, { getStoreEntity } from "services/store";
import Validator from "services/validator";
import {
  addonDeleteModalService,
  maintenancePolicyDetailsFetcher,
  maintenancePolicySelectionService,
  profilesFiltersFetcher,
  profilesSelectionService,
  deleteTemplateModalService,
  upgradeNowModalService,
  templateBasicInformationModalService,
  templatesTagsCache,
  inUseClustersActions,
  templatesActions,
  policiesMetadataCache,
  bulkVariableAssignmentService,
  variableAssignmentService,
} from "state/clusterdefinitions/services/templates";
import {
  getAssociatedPolicies,
  getRawTemplate,
  getSelectedMaintenancePolicyGuid,
  getVariablesEntities,
  getDraftMaintenancePolicyGuid,
  getSelectedProfiles,
  getTemplatePayload,
  getEditTemplate,
  getAssociatedProfiles,
} from "state/clusterdefinitions/selectors/templates";
import notifications from "services/notifications";
import i18next from "i18next";
import { formatTags } from "utils/presenters";
import {
  CLOUD_SELECTOR_MODULE,
  cloudSelectFormActions,
  selectCloudModal,
} from "state/selectCloud/services";
import { TEMPLATE_CREATE_STEPS } from "utils/constants";
import { WizardActions } from "modules/wizard/actions";
import isEqual from "lodash/isEqual";
import {
  policiesActions,
  wizardCloseModalService,
} from "state/clusterdefinitions/services/policies";
import { getCurrentProjectID } from "state/auth/selectors/common";
import api from "services/api";
import uniq from "lodash/uniq";
import dataFetcher from "modules/dataFetcher";
import { inUseClustersModalService } from "state/clusterdefinitions/services/templates";
import VariablesModule from "modules/variables";
import omit from "lodash/omit";

export function onEditTemplate(data) {
  return async (dispatch) => {
    const uid = data.metadata.uid;
    await dispatch(fetchCombinedTemplateProfiles());
    historyService.push(
      generatePath(CLUSTER_DEFINITIONS.TEMPLATE_OVERVIEW, {
        id: uid,
        tab: "overview",
      })
    );
  };
}

export function openBasicInformationFormDrawer() {
  return (dispatch) => {
    templateBasicInformationModalService.open().then(() =>
      dispatch(
        templateBasicInformationFormActions.submit({
          module: "templateBasicInformationForm",
        })
      )
    );
    dispatch(
      templateBasicInformationFormActions.init({
        module: "templateBasicInformationForm",
      })
    );
  };
}

export function deleteTemplate() {
  return (dispatch, getState) => {
    const data = getRawTemplate(getState());
    const { name, uid } = data?.metadata;
    deleteTemplateModalService.open({ name }).then(async () => {
      try {
        await api.delete(`v1/clusterTemplates/${uid}`);
        notifications.success({
          message: i18next.t("Template has been deleted successfully"),
        });
        const currentProjectId = getCurrentProjectID(getState());
        dispatch(clearTemplateTagsCache());
        dispatch(
          templatesActions.refreshItems(`templates/${currentProjectId}`)
        );
        historyService.push(
          generatePath(CLUSTER_DEFINITIONS.ROOT, { tab: "templates" })
        );
      } catch (error) {
        notifications.error({
          message: i18next.t(
            "Something went wrong while trying to delete the template"
          ),
          description: error.message,
        });
      }
    });
  };
}

const validateTemplateName = async (value) => {
  const promise = api.get(`v1/clusterTemplates/validate/name?name=${value}`);
  try {
    await promise;
  } catch (err) {
    if (err.code === "ResourceNameAlreadyExists") {
      return {
        result: i18next.t(
          `A cluster template with the name {{value}} already exists`,
          { value }
        ),
        field: "name",
      };
    } else {
      notifications.error({
        message: i18next.t(
          "Something went wrong while validating the cluster template name"
        ),
        description: err.message,
      });
    }
  }
  return false;
};
const nameValidationRule = DebouncedRule()(validateTemplateName);

const templateBasicInformationFormValidator = new Validator();
templateBasicInformationFormValidator.addRule("name", Missing());
templateBasicInformationFormValidator.addRule(
  ["tags"],
  areValidClusterProfileTags()
);
templateBasicInformationFormValidator.addRule(["name"], (value, key, data) => {
  const initialName =
    store.getState().forms?.templateBasicInformationForm?.initialData?.name ||
    "";
  if (!value || value === initialName) {
    return;
  }
  return templateNameValidationRule(value, key, data);
});

function templateNameValidationRule(value, key, data) {
  const isValid = isValidName({
    errorMessageText: i18next.t(
      "Template name must respect the naming guidelines"
    ),
    nameRegex: KUBERNETES_NAME_REGEX,
    rules: KUBERNETES_NAME_RULES,
    nameMaxSize: 63,
  })(value, key, data);
  if (isValid.invalidName) {
    return isValid.result;
  }
  const promise = nameValidationRule(value, key, data);
  store.dispatch({
    type: "TEMPLATE_NAME_VALIDATION",
    promise,
  });
  return promise;
}

function updateTemplatePayload(data) {
  const { name, description, tags } = data;
  const payload = {
    metadata: {
      name: name,
      annotations: {
        description: description,
      },
      labels: formatTags(tags),
    },
  };
  return payload;
}

export const templateBasicInformationFormActions = createFormActions({
  validator: templateBasicInformationFormValidator,
  init: async () => {
    const template = getRawTemplate(store.getState());
    const templateMetadata = template?.metadata || {};
    return Promise.resolve({
      name: templateMetadata.name || "",
      description: templateMetadata.annotations?.description || "",
      tags: parseTagsForInput(templateMetadata.labels) || [],
    });
  },
  submit: async (data) => {
    const template = getRawTemplate(store.getState());
    const templateUid = template?.metadata?.uid;
    const payload = updateTemplatePayload(data);
    try {
      await api.patch(`v1/clusterTemplates/${templateUid}/metadata`, payload);
      notifications.success({
        message: i18next.t("Template has been updated successfully"),
      });
      store.dispatch(clearTemplateTagsCache());
      historyService.push(
        generatePath(CLUSTER_DEFINITIONS.TEMPLATE_OVERVIEW, {
          id: templateUid,
          tab: "overview",
        })
      );
    } catch (error) {
      notifications.error({
        message: i18next.t(
          "Something went wrong while trying to update the template"
        ),
        description: error?.message,
      });
    }
  },
});

export function fetchClusterTemplateByUid(uid) {
  return async function thunk(dispatch) {
    const promise = api.get(`v1/clusterTemplates/${uid}`);
    dispatch({
      promise,
      type: "FETCH_TEMPLATE_DETAILS",
      schema: TemplateSchema,
    });
    try {
      await promise;
    } catch (error) {
      notifications.error({
        message: i18next.t(
          "Something went wrong while trying to fetch the template details"
        ),
        description: error?.message,
      });
    }
  };
}

export function fetchCombinedTemplateProfiles() {
  return async function thunk(dispatch, getState) {
    const template = getRawTemplate(getState());
    const uid = template?.metadata?.uid;
    if (!uid) {
      return;
    }

    const profileList = getAssociatedProfiles(getState());

    const profileDetailsPromises = profileList.map((profileTemplate) => {
      return dispatch({
        type: "TEMPLATES_FETCH_PROFILE_DETAILS",
        promise: api.get(
          `v1/clusterprofiles/${profileTemplate.profile.metadata.uid}`
        ),
        uid: profileTemplate.profile.metadata.uid,
      });
    });

    const profileListPromise = await dispatch({
      type: "FETCH_PROFILES_DETAILS",
      promise: api
        .get(`v1/spectroclusters/clusterTemplates/${uid}/profiles`)
        .then((response) => {
          const modifiedProfiles = response.profiles.map((profile) => {
            const { spec, ...profileWithoutSpec } = profile;
            const { packs: specPacks, ...specWithoutPacks } = spec || {};
            return {
              ...profileWithoutSpec,
              packs: specPacks?.map((pack) => ({
                ...pack?.spec,
                uid: pack?.metadata?.uid,
              })),
              spec: specWithoutPacks,
            };
          });
          return modifiedProfiles;
        }),
      schema: [ClusterProfileTemplateSchema],
    });

    dispatch({
      type: "FETCH_COMBINED_TEMPLATE_PROFILES",
      promise: Promise.allSettled([
        Promise.allSettled(profileDetailsPromises),
        profileListPromise,
      ]).then(([profileDetailsResults, profilesResult]) => {
        const successfulProfileDetails = [];
        if (profileDetailsResults.status === "fulfilled") {
          profileDetailsResults.value.forEach((result, index) => {
            if (result.status === "fulfilled" && result.value) {
              successfulProfileDetails.push(result.value);
            }
          });
        }
        const profiles =
          profilesResult.status === "fulfilled" ? profilesResult.value : [];

        return profiles.map((profile) => {
          const matchingDetail = successfulProfileDetails.find(
            (detail) => detail?.metadata?.uid === profile?.metadata?.uid
          );
          return {
            ...profile,
            ...(matchingDetail && {
              spec: {
                ...profile.spec,
                ...matchingDetail.spec,
                published: {
                  ...matchingDetail.spec?.published,
                  packs: (matchingDetail.spec?.published?.packs || []).map(
                    (existingPack) => {
                      const matchingProfilePack = (profile?.packs || []).find(
                        (pack) => pack.uid === existingPack.packUid
                      );
                      return matchingProfilePack
                        ? { ...existingPack, ...matchingProfilePack }
                        : existingPack;
                    }
                  ),
                },
              },
              metadata: { ...profile.metadata, ...matchingDetail.metadata },
            }),
          };
        });
      }),
      schema: [ClusterProfileTemplateSchema],
    });
  };
}

export function initTemplateOverview(uid, tab) {
  return async function thunk(dispatch) {
    await dispatch(fetchClusterTemplateByUid(uid));
    await dispatch(fetchCombinedTemplateProfiles());
    dispatch(fetchAssociatedClusters());
  };
}

export function fetchAssociatedClusters() {
  return async (dispatch, getState) => {
    const template = getRawTemplate(getState());
    const uid = template?.metadata?.uid;
    if (!uid) {
      return;
    }
    dispatch({
      type: "FETCH_CLUSTERS_DETAILS",
      promise: api.get(
        `v1/dashboard/clusterTemplates/${uid}/spectroclusters/meta`
      ),
      schema: [ClusterMetaSchema],
    });
  };
}

export const maintenancePolicySelectionActions = new ListActions({
  ...policiesActions,
  initialQuery: () => {
    return {
      limit: 25,
      policyType: ["maintenance"],
    };
  },
  filtersConfig: {
    ...policiesActions.filtersConfig,
    showExtendedFilters: false,
  },
});

export const profilesSelectionActions = new ListActions({
  hasPagination: true,
  persistFilters: true,
  schema: [ClusterProfileSchema],
  defaultQuery: {
    limit: 25,
  },
  initialQuery() {
    return {
      limit: 25,
    };
  },
  filtersFetcher: profilesFiltersFetcher,
  filtersConfig: {
    data: {
      profileName: {
        componentType: "search",
        search: true,
        placeholder: "Search by profile name",
        operator: "contains",
      },
    },
    showExtendedFilters: false,
  },
  async fetchData({
    offset,
    limit,
    sort,
    sortField,
    sortOrder,
    cloudTypes = [],
    profileTypes = [],
    continue: continueToken,
    profileName = "",
    resourceType = "clustertemplate",
  }) {
    const filters = {
      filter: {
        profileName: { contains: profileName },
        profileType: profileTypes,
        environment: cloudTypes,
        resourceType,
      },
      sort: sortField ? [{ field: sortField, order: sortOrder }] : sort,
    };
    const continueQueryParam = continueToken
      ? `&continue=${continueToken}`
      : "";

    const response = await api.post(
      `v1/dashboard/clusterprofiles?limit=${limit}&offset=${offset}${continueQueryParam}`,
      filters
    );

    return response;
  },
});

export function onTemplateCreate() {
  return () => {
    selectCloudModal
      .open({ redirectUrl: CLUSTER_DEFINITIONS.CREATE_TEMPLATE })
      .then(async () => {
        await store.dispatch(
          cloudSelectFormActions.submit({ module: CLOUD_SELECTOR_MODULE })
        );
        store.dispatch(clearTemplateTagsCache());
      });
  };
}

const templateValidator = new Validator();
templateValidator.addRule("name", Missing());
templateValidator.addRule(["name"], (value, key, data) => {
  return templateNameValidationRule(value, key, data);
});
templateValidator.addRule(
  "maintenancePolicyGuid",
  Missing({
    message: () =>
      i18next.t("At least one maintenance policy needs to be added"),
  })
);
templateValidator.addRule(
  "clusterProfiles",
  Missing({
    message: () =>
      i18next.t("At least one full or infra profile needs to be added"),
  })
);

templateValidator.addRule(["tags"], areValidClusterProfileTags());

function getPolicy() {
  const associatedPolicies = getAssociatedPolicies(store.getState());
  const selectedPolicy = associatedPolicies.find(
    (policy) => policy.spec.kind === "maintenance"
  );
  return selectedPolicy;
}

function resetEditMaintenancePolicyForm(policy) {
  return async (dispatch) => {
    const policyGuid = policy.guid;
    await dispatch(
      maintenancePolicyDetailsFetcher.fetch({
        uid: policy.metadata?.uid,
        type: "maintenance",
      })
    );
    dispatch(selectMaintenancePolicy(policyGuid));
    dispatch(setDraftMaintenancePolicyGuid(policyGuid));
  };
}

export const editMaintenancePolicyFormAction = createFormActions({
  init: async () => {
    const policy = getPolicy() || {};
    store.dispatch(setEditTemplate(true));
    store.dispatch(resetEditMaintenancePolicyForm(policy));
    return Promise.resolve({
      maintenancePolicyGuid: policy.guid,
    });
  },

  submit: async (data) => {
    try {
      const template = getRawTemplate(store.getState());
      const templateUid = template.metadata.uid;
      const maintenancePolicyGuid = data.maintenancePolicyGuid;
      const policy = getStoreEntity(maintenancePolicyGuid, PolicySchema);
      const payload = {
        policies: [
          {
            uid: policy.metadata.uid,
            kind: "maintenance",
          },
        ],
      };
      await api.patch(`v1/clusterTemplates/${templateUid}/policies`, payload);

      notifications.success({
        message: i18next.t("Maintenance policy has been updated successfully"),
      });

      await store.dispatch(fetchClusterTemplateByUid(templateUid));
      await store.dispatch(resetEditMaintenancePolicyForm(policy));
    } catch (error) {
      notifications.error({
        message: i18next.t(
          "Something went wrong while updating the maintenance policy"
        ),
        description: error.message,
      });
    }
  },
});

export const editTemplateProfileFormAction = createFormActions({
  init: async () => {
    store.dispatch(setEditTemplate(true));
    return Promise.resolve(resetEditProfileForm());
  },
  submit: async (data) => {
    const template = getRawTemplate(store.getState());
    const templateUid = template.metadata.uid;
    const inUseClusters = template.spec?.clusters?.length > 0;
    try {
      const profilesPayload = data.clusterProfiles.map((guid) => {
        let profile = getStoreEntity(guid, ClusterProfileSchema);
        if (!profile) {
          profile = getStoreEntity(guid, ClusterProfileTemplateSchema);
        }
        const uid = profile?.metadata?.uid;
        let variables = [];
        if (inUseClusters) {
          variables =
            data.variables?.find((variable) => variable.guid === guid)
              ?.variables || [];
        }
        return {
          uid: uid,
          variables,
        };
      });
      await api.put(`v1/clusterTemplates/${templateUid}/profiles`, {
        profiles: profilesPayload,
      });
      notifications.success({
        message: i18next.t("Profiles have been updated successfully"),
      });
      await store.dispatch(fetchClusterTemplateByUid(templateUid));
      store.dispatch(fetchCombinedTemplateProfiles());
    } catch (error) {
      notifications.error({
        message: i18next.t("Something went wrong while updating the profiles"),
        description: error.message,
      });
      return Promise.reject();
    }
  },
});

export const templateFormActions = createFormActions({
  validator: templateValidator,
  init: async () => {
    const { cloudType } = store.getState()?.router?.location?.query || {};
    store.dispatch(setEditTemplate(false));
    return Promise.resolve({
      name: "",
      description: "",
      tags: [],
      maintenancePolicyGuid: "",
      clusterProfiles: [],
      cloudType,
    });
  },
  submit: async () => {
    const payload = getTemplatePayload(store.getState());
    try {
      await api.post(`v1/clusterTemplates`, payload);

      notifications.success({
        message: i18next.t("Template has been created successfully"),
      });
      historyService.push(
        generatePath(CLUSTER_DEFINITIONS.ROOT, { tab: "templates" })
      );
    } catch (error) {
      notifications.error({
        message: i18next.t("Something went wrong while creating the template"),
        description: error.message,
      });
    }
  },
});

export const templateWizardActions = new WizardActions({
  formActions: templateFormActions,
  fieldsToValidate() {
    return {
      0: ["name", "tags"],
      1: ["maintenancePolicyGuid", "clusterProfiles"],
    };
  },
  getDescription({ id }) {
    const formData = store.getState().forms.template?.data;
    if (id === "basic-info") {
      return formData.name;
    }
    if (id === "template-config") {
      return i18next.t("Completed");
    }
  },
  steps: TEMPLATE_CREATE_STEPS,
});

export function clearTemplateTagsCache() {
  return function thunk(dispatch, getState) {
    const currentProjectID = getCurrentProjectID(getState());
    dispatch(templatesTagsCache.key(currentProjectID).flushCache());
    dispatch(policiesMetadataCache.key(currentProjectID).flushCache());
  };
}

export function initTemplateCreation() {
  return async function thunk(dispatch) {
    dispatch(templateWizardActions.initialize("template"));
    dispatch(maintenancePolicyDetailsFetcher.flushCache());
    await dispatch(
      templateFormActions.init({
        module: "template",
      })
    );
  };
}

export function closeTemplateWizard() {
  return (_, getState) => {
    const formData = getState().forms.template;

    if (isEqual(formData.data, formData.initialData)) {
      historyService.push(
        generatePath(CLUSTER_DEFINITIONS.ROOT, { tab: "templates" })
      );
      return;
    }

    wizardCloseModalService.open().then(() => {
      historyService.push(
        generatePath(CLUSTER_DEFINITIONS.ROOT, { tab: "templates" })
      );
    });
  };
}

function updateTemplateMaintenancePolicyGuid(guid) {
  return async (dispatch) => {
    dispatch(
      editMaintenancePolicyFormAction.onChange({
        module: "templateMaintenancePolicies",
        name: "maintenancePolicyGuid",
        value: guid,
      })
    );
  };
}

export function onAddMaintenancePolicy() {
  return async (dispatch, getState) => {
    const currentProjectId = getCurrentProjectID(getState());
    const isTemplateEdit = getEditTemplate(getState());
    try {
      await dispatch(
        maintenancePolicySelectionActions.initialize(
          `maintenancePolicySelection/${currentProjectId}`
        )
      );
      const selectedMaintenancePolicyGuid = getSelectedMaintenancePolicyGuid(
        getState()
      );
      store.dispatch(
        setDraftMaintenancePolicyGuid(selectedMaintenancePolicyGuid)
      );
      maintenancePolicySelectionService.open().then(() => {
        const selectedMaintenancePolicyGuid = getDraftMaintenancePolicyGuid(
          getState()
        );
        dispatch(selectMaintenancePolicy(selectedMaintenancePolicyGuid));
        const selectedMaintenancePolicy = getStoreEntity(
          selectedMaintenancePolicyGuid,
          PolicySchema
        );

        dispatch(
          maintenancePolicyDetailsFetcher.fetch({
            uid: selectedMaintenancePolicy?.metadata?.uid,
            type: "maintenance",
          })
        );
        if (isTemplateEdit) {
          dispatch(
            updateTemplateMaintenancePolicyGuid(selectedMaintenancePolicyGuid)
          );
        } else {
          dispatch(
            templateFormActions.onChange({
              module: "template",
              name: "maintenancePolicyGuid",
              value: selectedMaintenancePolicyGuid,
            })
          );
        }
      });
    } catch (error) {
      notifications.error({
        message: i18next.t(
          "Something went wrong while trying to fetch the maintenance policies"
        ),
        description: error.message,
      });
    }
  };
}

export function selectMaintenancePolicy(guid) {
  return async (dispatch) => {
    dispatch({
      type: "SET_SELECTED_MAINTENANCE_POLICY_GUID",
      guid,
    });
  };
}

export function setDraftMaintenancePolicyGuid(guid) {
  return async (dispatch) => {
    dispatch({
      type: "SET_INITIAL_SELECTED_MAINTENANCE_POLICY_GUID",
      guid,
    });
  };
}

export function setEditTemplate(isEditTemplate) {
  return async (dispatch) => {
    dispatch({
      type: "EDIT_TEMPLATE",
      isEditTemplate,
    });
  };
}

function initializeProfileSelection({ replaceGuid }) {
  return (dispatch, getState) => {
    const selectedProfiles = getSelectedProfiles(getState());
    const addons = selectedProfiles.filter(
      (profile) => profile.spec?.published.type === "add-on"
    );

    const profiles = replaceGuid
      ? [replaceGuid]
      : addons.map(({ guid }) => guid);
    dispatch(setSelectProfiles(profiles));
  };
}

function resetEditProfileForm() {
  const profileList = getAssociatedProfiles(store.getState());
  const template = getRawTemplate(store.getState());
  return {
    clusterProfiles: profileList.map((profile) => profile.guid),
    variables: profileList.map((profile) => {
      return {
        guid: profile.guid,
        variables: profile.variables || profile.spec?.variables || [],
      };
    }),
    cloudType: template?.spec?.cloudType,
  };
}

export function discardReplacedProfiles() {
  return async function thunk(dispatch, getState) {
    dispatch(
      editTemplateProfileFormAction.init({ module: "templateProfiles" })
    );
  };
}

export function discardReplacedPolicies() {
  return async function thunk(dispatch, getState) {
    const policy = getPolicy() || {};
    await dispatch(resetEditMaintenancePolicyForm(policy));
    dispatch(
      editMaintenancePolicyFormAction.batchChange({
        module: "templateMaintenancePolicies",
        updates: {
          maintenancePolicyGuid:
            getState().forms.templateMaintenancePolicies.initialData
              .maintenancePolicyGuid,
        },
      })
    );
  };
}

//TODO this function has to be modified once common modules are introduced
export function removeAddon(guid) {
  return (dispatch, getState) => {
    const editTemplate = getEditTemplate(getState());
    let selectedGuids = [];
    if (editTemplate) {
      selectedGuids = [
        ...(getState().forms?.templateProfiles?.data?.clusterProfiles || []),
      ];
    } else {
      selectedGuids = [
        ...(getState().forms?.template?.data?.clusterProfiles || []),
      ];
    }
    const index = selectedGuids.findIndex(
      (selectedGuid) => selectedGuid === guid
    );
    const selectedAddon = getStoreEntity(guid, ClusterProfileSchema);
    addonDeleteModalService
      .open({ name: selectedAddon?.metadata?.name })
      .then(() => {
        selectedGuids.splice(index, 1);

        if (editTemplate) {
          dispatch(
            editTemplateProfileFormAction.onChange({
              module: "templateProfiles",
              name: "clusterProfiles",
              value: selectedGuids,
            })
          );
        } else {
          dispatch(
            templateFormActions.onChange({
              module: "template",
              name: "clusterProfiles",
              value: selectedGuids,
            })
          );
        }
      });
  };
}

function fetchProfilesAndVariables(newGuids) {
  return async (dispatch, getState) => {
    const template = getRawTemplate(getState());
    const inUseClusters = template.spec?.clusters?.length > 0;
    const uniqueGuids = uniq(newGuids);
    try {
      const profileEntities = uniqueGuids.map((guid) => {
        let profile = getStoreEntity(guid, ClusterProfileTemplateSchema);
        if (!profile) {
          profile = getStoreEntity(guid, ClusterProfileSchema);
        }
        return profile;
      });
      let newVariables = [];
      if (inUseClusters) {
        newVariables = await Promise.all(
          uniqueGuids.map(async (guid, index) => {
            const entity = profileEntities[index];
            if (!entity?.metadata?.uid) {
              return { guid, variables: [] };
            }
            try {
              const response = await api.get(
                `v1/clusterprofiles/${entity.metadata.uid}/variables`
              );
              const variables = response?.variables || [];
              return {
                guid,
                variables: variables.map((variable) => ({
                  name: variable.name,
                  assignStrategy: "cluster",
                })),
              };
            } catch (error) {
              console.error(`Error fetching variables for ${guid}:`, error);
              return { guid, variables: [] };
            }
          })
        );
      }

      dispatch(
        editTemplateProfileFormAction.batchChange({
          module: "templateProfiles",
          updates: {
            clusterProfiles: uniqueGuids,
            variables: newVariables,
          },
        })
      );
    } catch (error) {
      console.error("Error fetching profile data:", error);
      dispatch(
        editTemplateProfileFormAction.batchChange({
          module: "templateProfiles",
          updates: { clusterProfiles: uniqueGuids },
        })
      );
    }
  };
}

//TODO: this has to be modified once common modules are introduced
export function onAddProfiles({
  profileTypes = ["infra", "cluster"],
  selectionType = "single",
  replaceGuid = "",
}) {
  return async (dispatch, getState) => {
    const currentProjectId = getCurrentProjectID(getState());
    let cloudType;
    const isEditTemplate = getEditTemplate(getState());
    if (isEditTemplate) {
      cloudType = getRawTemplate(getState())?.spec?.cloudType;
    } else {
      cloudType = getState().forms?.template?.data?.cloudType;
    }
    try {
      await dispatch(
        profilesSelectionActions.initialize(
          `profilesSelection/${currentProjectId}`,
          {
            profileTypes,
            cloudTypes: profileTypes.includes("add-on") ? [] : [cloudType],
            sort: [
              {
                field: "lastModifiedTimestamp",
                order: "desc",
              },
            ],
            limit: 25,
          }
        )
      );
      dispatch(initializeProfileSelection({ replaceGuid }));

      profilesSelectionService
        .open({
          selectionType,
          replaceGuid,
          addonsOnly: profileTypes.includes("add-on"),
        })
        .then(() => {
          const isEditTemplate = getEditTemplate(getState());
          const selectedProfileGuids =
            getState().templates?.configuration.selectedProfileGuids || [];
          const existingProfileGuids =
            (isEditTemplate
              ? getState().forms?.templateProfiles?.data?.clusterProfiles
              : getState().forms?.template?.data?.clusterProfiles) || [];

          let newGuids = [...existingProfileGuids];
          if (replaceGuid) {
            const idx = newGuids.findIndex((guid) => guid === replaceGuid);
            newGuids.splice(idx, 1, ...selectedProfileGuids);
          } else {
            newGuids = [...newGuids, ...selectedProfileGuids];
          }
          if (isEditTemplate) {
            dispatch(fetchProfilesAndVariables(newGuids));
          } else {
            dispatch(
              templateFormActions.onChange({
                module: "template",
                name: "clusterProfiles",
                value: uniq(newGuids),
              })
            );
          }
        });
    } catch (error) {
      notifications.error({
        message: i18next.t(
          "Something went wrong while trying to fetch the profiles"
        ),
        description: error.message,
      });
    }
  };
}

const profileVersionDataFetcher = dataFetcher({
  selectors: ["profileVersion"],
  schema: ClusterProfileSchema,
  async fetchData(_, uid) {
    return api.get(`v1/clusterprofiles/${uid}`);
  },
});

export function onVersionChange({ versionUid, version, profile }) {
  return async (dispatch, getState) => {
    if (version === profile.spec?.version) {
      return;
    }
    await dispatch(profileVersionDataFetcher.fetch(versionUid));
    const resp = profileVersionDataFetcher.selector(getState())?.result;
    const editTemplate = getEditTemplate(getState());
    let selectedProfilesGuids = [];
    if (editTemplate) {
      selectedProfilesGuids = [
        ...(getState().forms.templateProfiles?.data?.clusterProfiles || []),
      ];
    } else {
      selectedProfilesGuids = [
        ...(getState().forms.template?.data?.clusterProfiles || []),
      ];
    }
    const idx = selectedProfilesGuids.findIndex(
      (guid) => guid === profile.guid
    );
    selectedProfilesGuids.splice(idx, 1, resp.guid);
    if (editTemplate) {
      dispatch(fetchProfilesAndVariables(selectedProfilesGuids));
    } else {
      dispatch(
        templateFormActions.onChange({
          module: "template",
          name: "clusterProfiles",
          value: selectedProfilesGuids,
        })
      );
    }
  };
}

export function setSelectProfiles(selectedProfileGuids = []) {
  return (dispatch) => {
    dispatch({
      type: "SET_SELECTED_PROFILES",
      selectedProfileGuids,
    });
  };
}

export function showInUseClusters(clusterTemplates) {
  return async (dispatch, getState) => {
    const currentProjectId = getCurrentProjectID(getState());
    await dispatch(
      inUseClustersActions.initialize(`inUseClusters/${currentProjectId}`, {
        listView: "list",
        limit: 25,
        clusterTemplates,
      })
    );
    inUseClustersModalService.open();
  };
}

export function getClusterVariableIdentifier({ name, profileUid, clusterUid }) {
  return `${name}~${profileUid}~${clusterUid}`;
}

export function traceBackVariable(identifier) {
  const [name, profileUid, clusterUid] = identifier.split("~");
  return { name, profileUid, clusterUid };
}

export const variablesValuesListActions = new ListActions({
  hasPagination: false,
  persistFilters: true,
  schema: [VariableSchema],
  async fetchData({ assignmentState, name, clusterNames, profileUids }) {
    const template = getRawTemplate(store.getState());
    const templateUid = template?.metadata?.uid;
    const profiles = (template?.spec?.profiles || []).map(
      (profile) => profile.metadata?.uid
    );

    const promises = profiles.map((profileUid) => {
      return store.dispatch({
        type: "FETCH_PROFILE_VARIABLES",
        promise: api
          .get(
            `v1/clusterTemplates/${templateUid}/profiles/${profileUid}/variables`
          )
          .then((response) => {
            return (response.variables || []).map(({ variable, ...rest }) => ({
              ...rest,
              ...variable,
              profileUid,
            }));
          }),
        schema: [VariableSchema],
      });
    });

    const promise = Promise.allSettled(promises);

    store.dispatch({
      type: "FETCH_VARIABLES_VALUES",
      promise,
    });

    const data = await promise;
    const items = data
      .filter((data) => data.status === "fulfilled")
      .flatMap((layer) => layer.value)
      .filter((item) => {
        if (!name) return true;
        return (
          item.name.toLowerCase().includes(name.toLowerCase()) ||
          item.displayName.toLowerCase().includes(name.toLowerCase())
        );
      })
      .filter((item) => {
        if (!profileUids?.length) return true;
        return profileUids.includes(item.profileUid);
      })
      .map((item) => {
        const clusters = (item.clusters || [])
          .filter((cluster) => {
            const assignmentCond = assignmentState
              ? cluster.assignmentState === assignmentState
              : true;
            const clusterNamesCond = clusterNames?.length
              ? clusterNames.includes(cluster.clusterMeta?.metadata?.name)
              : true;

            return assignmentCond && clusterNamesCond;
          })
          .sort((a, b) =>
            a?.clusterMeta?.metadata?.name?.localeCompare(
              b?.clusterMeta?.metadata?.name
            )
          );

        return { ...item, clusters };
      })
      .filter((item) => !!item.clusters.length)
      .sort((a, b) => a?.name?.localeCompare(b?.name));

    return {
      items,
    };
  },
  fetchFilters() {
    const template = getRawTemplate(store.getState());
    const clustersOptions = (template.spec?.clusters || []).map(({ name }) => ({
      name,
      value: name,
      label: name,
    }));
    const profilesOptions = (template.spec?.profiles || [])
      .filter((profile) => !!profile.spec.variables.length)
      .map(({ profile }) => ({
        name: profile.metadata?.name,
        value: profile.metadata?.uid,
        label: profile.metadata?.name,
      }));

    return {
      name: {
        name: "name",
        type: "string",
      },
      assignmentState: {
        displayName: "Assignment",
        name: "assignmentState",
        type: "string",
        mode: "single",
        options: [
          {
            name: "assigned",
            value: "Assigned",
            label: "Assigned",
          },
          {
            name: "pending",
            value: "Pending",
            label: "Pending",
          },
        ],
      },
      profileUids: {
        displayName: "Profile Name",
        name: "profileUids",
        type: "string",
        options: profilesOptions,
      },
      clusterNames: {
        displayName: "Cluster Name",
        name: "clusterNames",
        type: "string",
        options: clustersOptions,
      },
    };
  },
  filtersConfig: {
    data: {
      name: {
        componentType: "search",
        search: true,
        placeholder: "Search by variable name...",
      },
      assignmentState: {
        locked: true,
        componentType: "dropdown",
      },
      profileUids: {
        locked: true,
        componentType: "dropdown",
      },
      clusterNames: {
        locked: true,
        componentType: "dropdown",
      },
    },
    order: ["assignmentState", "profileUids", "clusterNames"],
    showExtendedFilters: false,
  },
});

export function getVariableDetails(
  { name, profileUid } = {},
  profileVariables = []
) {
  const variable = profileVariables.find(
    (profileVariable) =>
      profileVariable.name === name && profileVariable.profileUid === profileUid
  );
  if (!variable) {
    return {};
  }

  return omit(variable, ["clusters", "profileUid", "profile"]);
}

const validator = new Validator();
validator.addRule("variables", function* (value, key, data) {
  const variables = getVariablesEntities(store.getState());

  for (const key of Object.keys(data.variables)) {
    const variableDetails = getVariableDetails(
      traceBackVariable(key),
      variables
    );
    yield {
      field: `variables.${key}`,
      result: isValidVariable({
        ...variableDetails,
      })({ value: data.variables[key] }),
    };
  }
});

export const variableValuesFormActions = createFormActions({
  validator,
  init: async () => {
    const variableEntities = getVariablesEntities(store.getState());
    const variables = variableEntities.reduce(
      (
        accum,
        { name, profileUid = "", clusters = [], format, inputType = "text" }
      ) => {
        clusters.forEach((cluster) => {
          const isAssigned =
            cluster?.assignmentState?.toLowerCase() === "assigned";
          const runningValue = isAssigned ? cluster.assignedValue : "";

          const identifier = getClusterVariableIdentifier({
            name: name,
            profileUid,
            clusterUid: cluster.uid,
          });

          if (format === "boolean" && inputType === "text") {
            accum[identifier] = runningValue === "true";
          } else {
            accum[identifier] = runningValue;
          }
        });
        return accum;
      },
      {}
    );
    return Promise.resolve({ variables });
  },
  submit: async (data) => {
    return data;
  },
});

export function setSelectedVariables(selectedVariables = []) {
  return (dispatch) => {
    dispatch({
      type: "SET_SELECTED_VARIABLES",
      selectedVariables,
    });
  };
}

function formatAssignVariablesPayload(changes) {
  const profiles = changes.reduce((acc, item) => {
    const { profileUid, clusterUid, name, value } = item;

    if (!acc[profileUid]) {
      acc[profileUid] = { uid: profileUid, variables: [] };
    }

    const profile = acc[profileUid];
    let variable = profile.variables.find((v) => v.name === name);
    if (!variable) {
      variable = { name, clusters: [] };
      profile.variables.push(variable);
    }
    variable.clusters.push({ uid: clusterUid, value: value?.toString() || "" });

    return acc;
  }, {});

  return {
    profiles: Object.values(profiles),
  };
}

export const templateVariablesModule = new VariablesModule({
  name: "template-variable-values",
  selectors: {
    getProfiles: (state) => {
      const selectedRows =
        bulkVariableAssignmentService?.data?.selectedRows || [];
      if (!selectedRows.length) {
        return [];
      }

      const variables = getVariablesEntities(store.getState());
      const variableDetails = traceBackVariable(selectedRows[0]);
      const variableEntity = variables.find((variable) => {
        return (
          variable.name === variableDetails.name &&
          variable.profileUid === variableDetails.profileUid
        );
      });

      return [
        {
          ...variableEntity?.profile,
          variables: [
            {
              ...omit(variableEntity, ["profile", "clusters", "profileUid"]),
            },
          ],
        },
      ];
    },
  },
  onSubmit: async ({ variables }) => {
    const selectedRows =
      bulkVariableAssignmentService?.data?.selectedRows || [];
    const variableEntities = getVariablesEntities(store.getState());

    const changes = selectedRows.map((selectedRow) => {
      const { name, profileUid, clusterUid } = traceBackVariable(selectedRow);
      const variable = variableEntities.find((variable) => {
        return variable.name === name && variable.profileUid === profileUid;
      });
      return {
        profileUid,
        clusterUid,
        name,
        value: variables[variable?.guid]?.value,
      };
    });
    const template = getRawTemplate(store.getState());
    const payload = formatAssignVariablesPayload(changes);

    try {
      await api.patch(
        `v1/clusterTemplates/${template?.metadata?.uid}/profiles/variables`,
        payload
      );
      notifications.success({
        message: i18next.t("Variable values have been assigned successfully"),
      });
    } catch (error) {
      notifications.error({
        message: i18next.t(
          "Something went wrong while trying to assign the variable values"
        ),
        description: error.message,
      });
      return;
    }

    store.dispatch(onRefreshItems());
  },
});

export function onRefreshItems() {
  return async (dispatch, getState) => {
    const currentProjectId = getCurrentProjectID(getState());
    await dispatch(
      variablesValuesListActions.initialize(`variables/${currentProjectId}`)
    );
    await dispatch(
      variableValuesFormActions.init({ module: "variablesValues" })
    );
  };
}

export function onAssignVariableValue(identifier) {
  return async (dispatch, getState) => {
    variableAssignmentService.open().then(async () => {
      const variableEntities = getVariablesEntities(store.getState());
      const form = store.getState().forms["variablesValues"];
      const formData = form?.data?.variables || {};
      const initialData = form?.initialData?.variables || {};
      const changes = Object.keys(formData)
        .filter((key) => key.startsWith(identifier))
        .reduce((accum, key) => {
          // TO DO: temporary fix for boolean variables until diffing module is introduced
          const traced = traceBackVariable(key);
          const variableEntity = variableEntities.find(
            ({ name, profileUid }) => {
              return name === traced.name && profileUid === traced.profileUid;
            }
          );
          if (!variableEntity) {
            return accum;
          }

          const { format, inputType, clusters } = variableEntity;
          const isBoolean = format === "boolean" && inputType !== "dropdown";

          const isPendingCluster = (clusters || []).find(
            ({ uid, assignmentState }) =>
              uid === traced.clusterUid &&
              assignmentState?.toLowerCase() === "pending"
          );
          if (
            (isBoolean && isPendingCluster) ||
            formData[key] !== initialData[key]
          ) {
            accum.push({
              ...traced,
              value: formData[key],
            });
          }
          return accum;
        }, []);
      const template = getRawTemplate(store.getState());
      const payload = formatAssignVariablesPayload(changes);

      try {
        await api.patch(
          `v1/clusterTemplates/${template?.metadata?.uid}/profiles/variables`,
          payload
        );
        notifications.success({
          message: i18next.t("Variable values have been assigned successfully"),
        });
      } catch (error) {
        notifications.error({
          message: i18next.t(
            "Something went wrong while trying to assign the variable values"
          ),
          description: error.message,
        });
        return;
      }

      dispatch(onRefreshItems());
    });
  };
}

export function onAssignBulkVariableValues(selectedRows) {
  return async (dispatch, getState) => {
    bulkVariableAssignmentService.open({ selectedRows }).then(async () => {
      await templateVariablesModule.actions.submit();
      dispatch(setSelectedVariables([]));
    });
    templateVariablesModule.actions.initialize();
  };
}

export function upgradeTemplate() {
  return async (dispatch, getState) => {
    upgradeNowModalService.open().then(async () => {
      const template = getRawTemplate(getState());
      const uid = template?.metadata?.uid;
      try {
        await api.patch(
          `v1/spectroclusters/clusterTemplates/${uid}/clusters/upgrade`
        );
        notifications.success({
          message: i18next.t("Upgrade now is successfully initiated"),
        });
        dispatch(fetchClusterTemplateByUid(uid));
      } catch (error) {
        notifications.error({
          message: i18next.t(
            "Something went wrong while trying to upgrade the template"
          ),
          description: error.message,
        });
      }
    });
  };
}
